import React from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, Diamond, Folder, LoaderCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import HealthBadge from '../components/HealthBadge'
import { INDENT_PX, MAX_DURATION_DAYS, ROW_H, type ColumnId, MAX_OWNER_LENGTH } from '../lib/constants'
import { formatDays, formatIsoDate } from '../lib/format'
import type { Tree } from '../lib/tree'
import type { ScheduleActivity } from '../types'
import DateCell from './cells/DateCell'
import IntCell from './cells/IntCell'
import PredecessorsCell from './cells/PredecessorsCell'
import TextCell from './cells/TextCell'
import type { CellMove } from './cells/CellEditor'
import { isNumericColumn } from './gantt-columns'

/** A predecessor (or, for the Task 16 context menu, successor) living in another schedule (§8.6):
 * rendered as a read-only chip after the editable local tokens. `id`/`scheduleId` identify the
 * foreign activity for navigation (Task 16's Go to predecessor/successor), unused by the chip itself. */
export interface ForeignPredecessor { scheduleName: string; wbsCode: string; name: string; id: string; scheduleId: string }

const EMPTY_FOREIGN: ForeignPredecessor[] = []

export interface GanttRowProps {
  activity: ScheduleActivity
  depth: number
  rowNumber: number
  hasChildren: boolean
  collapsed: boolean
  selected: boolean
  focused: boolean
  top: number
  gridWidth: number
  columns: ColumnId[]
  columnWidths: Record<ColumnId, number>
  readOnly: boolean
  tree: Tree<ScheduleActivity>
  predecessorsText?: string
  foreignPredecessors?: ForeignPredecessor[]
  /** The column currently being edited on THIS row, or null — kept a primitive so unrelated rows don't re-render. */
  editingColumn: ColumnId | null
  /** The column of THIS row whose mutation is in flight, or null. */
  pendingColumn: ColumnId | null
  /** The value entered for `pendingColumn`, shown at reduced opacity next to a spinner while the mutation is in flight. */
  pendingValue: string | null
  onToggle: (id: string) => void
  onClick: (id: string, e: React.MouseEvent) => void
  onDoubleClick: (id: string, column: ColumnId) => void
  onContextMenu: (id: string, e: React.MouseEvent) => void
  onCommitCell: (id: string, column: ColumnId, raw: string, move: CellMove) => void
  onCancelCell: () => void
}

const GanttRow: React.FC<GanttRowProps> = ({
  activity, depth, rowNumber, hasChildren, collapsed, selected, focused, top, gridWidth, columns, columnWidths, readOnly, tree, predecessorsText, foreignPredecessors = EMPTY_FOREIGN,
  editingColumn, pendingColumn, pendingValue,
  onToggle, onClick, onDoubleClick, onContextMenu, onCommitCell, onCancelCell,
}) => {
  const { t } = useTranslation()

  const pending = (text: string): React.ReactNode => (
    <span className="flex items-center gap-1 truncate">
      <span className="truncate opacity-60">{text}</span>
      <LoaderCircle className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
    </span>
  )
  const commit = (column: ColumnId) => (v: string, move: CellMove) => onCommitCell(activity.id, column, v, move)

  const cellContent = (id: ColumnId): React.ReactNode => {
    switch (id) {
      case 'rowNumber':
        return rowNumber
      case 'wbs':
        return activity.wbsCode
      case 'owner':
        return editingColumn === 'owner'
          ? <TextCell value={activity.owner ?? ''} maxLength={MAX_OWNER_LENGTH} allowEmpty errorKey="Schedule_cell_invalid_owner" onCommit={commit('owner')} onCancel={onCancelCell} />
          : pendingColumn === 'owner'
            ? pending(pendingValue ?? '')
            : <span className="truncate">{activity.owner}</span>
      case 'name': {
        const label = editingColumn === 'name'
          ? <TextCell value={activity.name} onCommit={commit('name')} onCancel={onCancelCell} />
          : pendingColumn === 'name'
            ? pending(pendingValue ?? '')
            : <span className={cn('truncate', activity.isSummary && 'font-medium')}>{activity.name}</span>
        return (
          <>
            {hasChildren ? (
              <button
                type="button"
                className="h-4 w-4 shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground"
                aria-label={t(collapsed ? 'Schedule_expand' : 'Schedule_collapse')}
                onClick={(e) => { e.stopPropagation(); onToggle(activity.id) }}
                onDoubleClick={(e) => e.stopPropagation()}
              >
                {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            ) : (
              <span className="h-4 w-4 shrink-0" />
            )}
            {activity.isMilestone && <Diamond className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            {activity.isSummary && <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            {label}
          </>
        )
      }
      case 'duration':
        if (editingColumn === 'duration') return <IntCell value={String(activity.durationDays)} min={0} max={MAX_DURATION_DAYS} errorKey="Schedule_cell_invalid_int" onCommit={commit('duration')} onCancel={onCancelCell} />
        if (pendingColumn === 'duration') return pending(formatDays(t, Number(pendingValue)))
        return formatDays(t, activity.durationDays)
      case 'start':
        if (editingColumn === 'start') return <DateCell value={activity.start} onCommit={commit('start')} onCancel={onCancelCell} />
        if (pendingColumn === 'start') return pending(formatIsoDate(t, pendingValue))
        return formatIsoDate(t, activity.start)
      case 'finish':
        if (editingColumn === 'finish') return <DateCell value={activity.finish} onCommit={commit('finish')} onCancel={onCancelCell} />
        if (pendingColumn === 'finish') return pending(formatIsoDate(t, pendingValue))
        return formatIsoDate(t, activity.finish)
      case 'predecessors': {
        if (editingColumn === 'predecessors') return <PredecessorsCell activityId={activity.id} value={predecessorsText ?? ''} tree={tree} onCommit={commit('predecessors')} onCancel={onCancelCell} />
        // Review fix (parked minor): foreign-predecessor chips used to be inside the pending
        // branch (hidden while the local predecessors text is mid-mutation) — keep them outside,
        // same as the name column's milestone/summary icons.
        return (
          <>
            {pendingColumn === 'predecessors' ? pending(pendingValue ?? '') : predecessorsText}
            {foreignPredecessors.map((f, i) => (
              <TooltipProvider key={`${f.scheduleName}-${f.wbsCode}-${i}`} delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex shrink-0">
                      <Badge variant="outline" className="text-[10px] px-1 py-0 ml-1">⧉ {f.scheduleName} {f.wbsCode}</Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{f.scheduleName} · {f.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </>
        )
      }
      case 'percent':
        if (editingColumn === 'percent') return <IntCell value={String(activity.percentComplete)} min={0} max={100} errorKey="Schedule_cell_invalid_percent" onCommit={commit('percent')} onCancel={onCancelCell} />
        if (pendingColumn === 'percent') return pending(pendingValue ?? '')
        return activity.isSummary
          ? <span className="italic text-muted-foreground">{activity.rolledPercent}</span>
          : activity.percentComplete
      case 'float':
        return activity.totalFloatDays
      case 'health':
        return <HealthBadge health={activity.health} iconOnly />
      default:
        return null
    }
  }

  return (
    <div
      data-gantt-row
      data-activity-id={activity.id}
      className={cn(
        'absolute left-0 flex items-center border-b text-sm',
        selected ? 'bg-accent' : 'hover:bg-muted/50',
        focused && 'ring-1 ring-inset ring-primary',
        readOnly && 'cursor-default',
      )}
      // ui-drift: dynamic
      style={{ top, height: ROW_H, width: gridWidth }}
      onClick={(e) => onClick(activity.id, e)}
      onContextMenu={(e) => onContextMenu(activity.id, e)}
    >
      {columns.map((id) => (
        <div
          key={id}
          data-column={id}
          className={cn(
            'shrink-0 h-full px-2 flex items-center gap-1 truncate',
            isNumericColumn(id) && 'justify-end tabular-nums',
            (id === 'start' || id === 'finish') && 'tabular-nums',
            id === 'rowNumber' && 'text-muted-foreground',
            // review fix (parked minor, spec §9.1): red only when float is 0 AND the row is critical
            id === 'float' && activity.isCritical && activity.totalFloatDays === 0 && 'text-destructive',
            id === 'health' && 'justify-center',
          )}
          // ui-drift: dynamic
          style={id === 'name'
            ? { width: columnWidths[id], paddingLeft: 8 + depth * INDENT_PX }
            : { width: columnWidths[id] }}
          onDoubleClick={() => onDoubleClick(activity.id, id)}
        >
          {cellContent(id)}
        </div>
      ))}
    </div>
  )
}

export default React.memo(GanttRow)
